import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import coverImage from '../../img/xray_logo_vert.png';
import Logo from '../../img/fpg_logo.png';
import lonelyHandsCover from '../../img/lh_title.png'
import NewsletterSignup from '../NewsletterSignup.jsx';

export default function HomePage() {
  // --- RESPONSIVE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- STYLES (Moved inside to access isMobile) ---
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      backgroundColor: '#0a1128', 
      color: '#f8fafc', 
      fontFamily: '"Inter", sans-serif',
    },
    hero: {
      minHeight: '85vh', // Changed to minHeight so content doesn't overflow on small phones
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'radial-gradient(circle at 50% 50%, #15234b 0%, #0a1128 100%)', 
      textAlign: 'center',
      padding: isMobile ? '100px 20px 60px 20px' : '0 20px', // Extra top padding on mobile for the header
    },
    heroHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '15px' : '25px', // Tighter gap on mobile
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    heroLogo: {
      height: isMobile ? '60px' : '100px', // Shrink logo on mobile
      width: 'auto',
      objectFit: 'contain',
      filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))', 
    },
    heroTitle: {
      fontSize: isMobile ? '3rem' : '5rem', // CRITICAL FIX: Shrink the giant text!
      fontWeight: '900',
      margin: 0,
      letterSpacing: '-2px',
      background: 'linear-gradient(45deg, #ffffff, #b026ff)', 
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      color: 'transparent',
      lineHeight: '1',
    },
    heroSubtitle: {
      fontSize: isMobile ? '1.1rem' : '1.5rem',
      color: '#94a3b8', 
      marginBottom: '50px',
      maxWidth: '650px',
      lineHeight: '1.6',
      fontWeight: '400',
    },
    ctaButton: {
      padding: isMobile ? '15px 40px' : '18px 50px',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: '#fff',
      backgroundColor: '#b026ff', 
      border: 'none',
      borderRadius: '50px',
      cursor: 'pointer',
      textDecoration: 'none',
      boxShadow: '0 10px 25px rgba(176, 38, 255, 0.3)',
    },
    section: {
      padding: isMobile ? '60px 20px' : '100px 20px', // Less padding on mobile
      width: '100%',
      maxWidth: '1200px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    sectionTitle: {
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: '800',
      marginBottom: '60px',
      color: '#f8fafc', 
      position: 'relative',
      textAlign: 'center',
    },
    sectionUnderline: {
      position: 'absolute', bottom: '-15px', left: '50%', transform: 'translateX(-50%)', 
      width: '60px', height: '4px', backgroundColor: '#b026ff', borderRadius: '2px'
    },
    grid: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '40px',
      width: '100%',
    },
    card: {
      backgroundColor: '#162447', 
      borderRadius: '16px',
      overflow: 'hidden',
      width: isMobile ? '100%' : '380px', // CRITICAL FIX: Cards will now shrink to fit the phone!
      maxWidth: '380px', // Ensures they don't get too wide on tablets
      boxShadow: '0 20px 40px rgba(0,0,0,0.4)', 
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #2a3b61', 
    },
    cardImage: {
      height: '220px',
      width: '100%',
      objectFit: 'cover',
      borderBottom: '1px solid #2a3b61',
    },
    cardContent: {
      padding: isMobile ? '20px' : '30px', // Shrink padding so text has room to breathe
      textAlign: 'left',
    },
    cardTitle: {
      fontSize: isMobile ? '1.5rem' : '1.8rem', // Slightly smaller title
      fontWeight: 'bold',
      marginBottom: '10px',
      color: '#ffffff', 
    },
    cardText: {
      color: '#94a3b8', // Light slate-blue/grey
      lineHeight: '1.6',
      marginBottom: '25px',
    },
    textLink: {
      color: '#b026ff', // Signature purple stays!
      fontWeight: 'bold',
      textDecoration: 'none',
      fontSize: '1.1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    cardHeaderRow: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'
    },
    badge3X: {
      fontSize: '0.75rem', backgroundColor: 'rgba(255, 0, 127, 0.1)', color: '#ff007f', 
      padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold'
    },
    badgeLonely: {
      fontSize: '0.75rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', 
      padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold', border: '1px solid #10b981'
    },
    socialSection: {
      padding: isMobile ? '50px 20px' : '80px 20px', // Tighter spacing
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      backgroundColor: '#111c3a', 
    },
    socialTitle: {
      fontSize: isMobile ? '1.5rem' : '2rem', // Scale down the header
      marginBottom: '20px', 
      fontWeight: 'bold', 
      color: '#f8fafc',
      textAlign: 'center', // Ensures it stays centered if it wraps
    },
    socialText: {
      color: '#94a3b8', marginBottom: '30px', textAlign: 'center' // Slate grey for dark mode
    },
    socialButtonGroup: {
      display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center'
    }
  };
  return (
    <div style={styles.container}>
      
      {/* --- HERO SECTION --- */}
      <div style={styles.hero}>
        <div style={styles.heroHeader}>
          <img src={Logo} style={styles.heroLogo} alt="Logo" />
          <h1 style={styles.heroTitle}>FOREPLAY GAMES</h1>
        </div>
        <p style={styles.heroSubtitle}>
          Adult gaming with a heartbeat.
          We craft narrative-driven games where real intimacy, deep connections, and meaningful romance come first.
        </p>
        <div data-companion-hint="This is for our newsletter! Sign up to be the first to know when our new games and updates drop. I might even slip a few exclusive teasers in there just for you~">
          <NewsletterSignup buttonStyle={styles.ctaButton}/>
        </div>
      </div>

      {/* --- CURRENT PROJECTS --- */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          Current Projects
          <span style={styles.sectionUnderline}/>
        </h2>
        
        <div style={styles.grid}>
          
          {/* CARD 1: 3XRay */}
          <div
            style={styles.card}
            data-companion-hint="This one is 3XRay. A succubus gives you X-ray vision, and you get to decide if you want to be a hero, or... well, the villain I suppose. Choose wisely!"
          >
            <img 
              src={coverImage} 
              alt="3XRay Cover Art" 
              style={styles.cardImage} 
            />
            
            <div style={styles.cardContent}>
              <div style={styles.cardHeaderRow}>
                <h3 style={{ ...styles.cardTitle, margin: 0 }}>3XRay</h3>
                {/* <span style={styles.badge3X}>AI-ASSISTED</span> */}
              </div>

              <p style={styles.cardText}>
                Use your X-Ray vision to uncover secrets, navigate social hierarchies, and find romance where you least expect it.
              </p>
              
              <Link to="/games/3xr" style={styles.textLink}>
                View Game Page →
              </Link>
            </div>
          </div>

          {/* CARD 2: LonelyHands */}
          <div
            style={styles.card}
            data-companion-hint="That's LonelyHands! You get to manage a content platform and romance the creators. You'll definitely want to check this one out, I'm actually waiting for you in there. Don't keep me waiting~"
          >
            <img 
              src={lonelyHandsCover} 
              alt="LonelyHands Cover Art" 
              style={styles.cardImage} 
            />
            
            <div style={styles.cardContent}>
              <div style={styles.cardHeaderRow}>
                <h3 style={{ ...styles.cardTitle, margin: 0 }}>LonelyHands</h3>
                {/* <span style={styles.badgeLonely}>NO AI</span> */}
              </div>

              <p style={styles.cardText}>
                A minimalist, pixel-art desktop simulator. Pay your tuition by ghostwriting for top models on a subscription platform, balancing your digital hustle with your real life.
              </p>
              
              <Link to="/games/lonelyhands" style={styles.textLink}>
                View Game Page →
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* --- NEWSLETTER / SOCIALS --- */}
      <div style={styles.socialSection}>
        <h2 style={styles.socialTitle}>Join The Community</h2>
        <p style={styles.socialText}>
          Get exclusive sketches, devlogs, and early builds.
        </p>
        
        <div style={styles.socialButtonGroup}>
           
           {/* TWITTER / X */}
           <a 
             href="https://x.com/ForeplayGames" 
             target="_blank" 
             rel="noopener noreferrer"
             style={{...styles.ctaButton, backgroundColor: '#1da1f2', fontSize: '1rem'}} // Twitter Blue instead of Black so it pops on dark mode
             data-companion-hint="That'll take you to our Twitter/X Page! That's where we keep all the good previews, if you know what I mean~"
           >
             Twitter / X
           </a>

           {/* DISCORD */}
           <button 
             style={{...styles.ctaButton, backgroundColor: '#5865F2', fontSize: '1rem', cursor: 'not-allowed', opacity: 0.7}}
             title="Discord Community Opening Soon!"
             data-companion-hint="That's our Discord link! It's still being worked on for the moment, but once it's open, it'll be the best place to hang out with us!"
           >
             Discord (Coming Soon)
           </button>

        </div>
      </div>
    </div>
  );
}